import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../api/client';
import {
  TEXT_DARK,
  TEXT_MUTED,
  TEXT_SECONDARY,
  BORDER_LIGHT,
  BG_SURFACE,
  FONT_HEADING,
} from '../legacy-ball/table-management/designTokens';

export interface GroupMember {
  groupId: string;
  userId: string;
  joinedAt: string;
  user: {
    id: string;
    fullName: string;
    email: string;
    role: string;
  };
}

interface UserSearchResult {
  id: string;
  fullName: string;
}

interface GroupMemberListProps {
  groupId: string;
  groupName: string;
}

const iconBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '0.9rem',
  borderRadius: '4px',
  lineHeight: 1,
  transition: 'background-color 0.15s',
};

const memberStyles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1rem 0 0',
  },
  heading: {
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    fontSize: '0.95rem',
    color: TEXT_DARK,
    margin: '0 0 0.75rem',
  },
  searchContainer: {
    position: 'relative',
    marginBottom: '1rem',
  },
  searchInput: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
    outline: 'none',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: `1px solid ${BORDER_LIGHT}`,
    borderTop: 'none',
    borderRadius: '0 0 4px 4px',
    maxHeight: '200px',
    overflowY: 'auto',
    zIndex: 10,
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
  dropdownItem: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    color: TEXT_DARK,
    cursor: 'pointer',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    transition: 'background-color 0.1s',
  },
  dropdownItemHover: {
    backgroundColor: BG_SURFACE,
  },
  dropdownEmpty: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
  memberRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.5rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  memberInfo: {
    flex: 1,
    minWidth: 0,
  },
  memberName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  },
  memberMeta: {
    fontSize: '0.75rem',
    color: TEXT_SECONDARY,
    margin: '0.15rem 0 0',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '1.5rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.82rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px dashed ${BORDER_LIGHT}`,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.4rem 0.6rem',
    borderRadius: '4px',
    marginBottom: '0.75rem',
    fontSize: '0.8rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '1rem',
    color: TEXT_MUTED,
    fontSize: '0.82rem',
  },
};

export function GroupMemberList({ groupId, groupName }: GroupMemberListProps) {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  async function fetchMembers() {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient<GroupMember[]>(`/event-groups/${groupId}/members`);
      setMembers(data);
    } catch {
      setError('Failed to load members.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchMembers();
  }, [groupId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  function handleSearchChange(value: string) {
    setSearchQuery(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        setSearching(true);
        const results = await apiClient<UserSearchResult[]>(
          `/users/members?search=${encodeURIComponent(value.trim())}`
        );
        // Filter out users already in the group
        const memberUserIds = new Set(members.map((m) => m.userId));
        const filtered = results.filter((u) => !memberUserIds.has(u.id));
        setSearchResults(filtered);
        setShowDropdown(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }

  async function handleAddMember(userId: string) {
    try {
      setError('');
      await apiClient(`/event-groups/${groupId}/members`, {
        method: 'POST',
        body: { userId },
      });
      setSearchQuery('');
      setSearchResults([]);
      setShowDropdown(false);
      await fetchMembers();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to add member.');
    }
  }

  async function handleRemoveMember(userId: string) {
    try {
      setError('');
      await apiClient(`/event-groups/${groupId}/members/${userId}`, {
        method: 'DELETE',
      });
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to remove member.');
    }
  }

  return (
    <div style={memberStyles.container}>
      <h4 style={memberStyles.heading}>
        Members ({members.length})
      </h4>

      {error && <div style={memberStyles.error}>{error}</div>}

      {/* Search-to-add */}
      <div style={memberStyles.searchContainer} ref={containerRef}>
        <input
          type="text"
          style={memberStyles.searchInput}
          placeholder="Search users to add…"
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => {
            if (searchResults.length > 0) setShowDropdown(true);
          }}
          aria-label={`Search users to add to ${groupName}`}
        />
        {showDropdown && (
          <div style={memberStyles.dropdown} role="listbox" aria-label="User search results">
            {searching ? (
              <div style={memberStyles.dropdownEmpty}>Searching…</div>
            ) : searchResults.length === 0 ? (
              <div style={memberStyles.dropdownEmpty}>
                No users found matching "{searchQuery}"
              </div>
            ) : (
              searchResults.map((user, index) => (
                <div
                  key={user.id}
                  style={{
                    ...memberStyles.dropdownItem,
                    ...(hoveredIndex === index ? memberStyles.dropdownItemHover : {}),
                  }}
                  onClick={() => handleAddMember(user.id)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                  role="option"
                  aria-label={`Add ${user.fullName}`}
                >
                  {user.fullName}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Member List */}
      {loading ? (
        <div style={memberStyles.loading}>Loading members…</div>
      ) : members.length === 0 ? (
        <div style={memberStyles.emptyState}>
          No members yet. Use the search above to add users.
        </div>
      ) : (
        <div>
          {members.map((member) => (
            <div key={member.userId} style={memberStyles.memberRow}>
              <div style={memberStyles.memberInfo}>
                <p style={memberStyles.memberName}>
                  {member.user.fullName}
                </p>
                <p style={memberStyles.memberMeta}>
                  {member.user.email} · {member.user.role}
                </p>
              </div>
              <button
                style={iconBtn}
                onClick={() => handleRemoveMember(member.userId)}
                title="Remove member"
                aria-label={`Remove ${member.user.fullName} from ${groupName}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
